import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngxs/store';
import { TokenService } from '../../services/token/token.service';
import { SetUserInfo } from '../../../store/auth/auth.actions';
import { FileUploadService } from '../../services/file-upload/file-upload.service';
import { Convert } from '../../utils/ConvertMimeTypes';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-screening-mobile-upload-evidences',
  templateUrl: './screening-mobile-upload-evidences.component.html',
  styleUrls: ['./screening-mobile-upload-evidences.component.sass'],
})
export class ScreeningMobileUploadEvidencesComponent implements OnInit {
  params: Record<string, any> = {};
  previewUrl: string | null = null;
  isUploading = false;
  isLoadingToken = true;
  tokenError: string | null = null;
  uploadSuccess = false;

  logoUrl = 'assets/svgs/logo.svg';

  constructor(
    private route: ActivatedRoute,
    private tokenService: TokenService,
    private store: Store,
    private fileUploadService: FileUploadService,
    private toastr: ToastrService
  ) {}

  async ngOnInit(): Promise<void> {
    this.route.queryParams.subscribe((queryParams) => {
      this.params = { ...queryParams };

      if (this.params['q']) {
        try {
          const decodedBase64 = decodeURIComponent(this.params['q']);
          const jsonString = atob(decodedBase64);
          this.params['question'] = JSON.parse(jsonString);
          delete this.params['q'];
        } catch (err) {
          console.error('Erro ao decodificar question:', err);
        }
      }
    });

    await this.loadToken();
  }

  private async loadToken(): Promise<void> {
    const idToken = this.getValue('i');

    try {
      const tokenResponse = await this.tokenService
        .get(idToken, 'COLLECT_TVF.getToken')
        .pipe(take(1))
        .toPromise();

      const token = tokenResponse?.token ?? tokenResponse?.Token;

      this.handleTokenResponse(token);
    } catch (err) {
      console.error('Erro ao obter token:', err);
      this.tokenError =
        'Falha ao obter o token para upload de imagens. Tente gerar um novo QR Code pela triagem no computador.';
      this.toastr.error('Erro de autenticação.');
    } finally {
      this.isLoadingToken = false;
    }
  }

  private handleTokenResponse(token: any): void {
    if (token) {
      this.store.dispatch(new SetUserInfo({ token }));
    } else {
      this.tokenError = 'Token inválido recebido. Tente gerar um novo QR Code.';
    }
  }

  async uploadEvidence() {
    if (!this.previewUrl || this.isUploading) return;

    this.isUploading = true;

    try {
      const questionOrder = this.params['o'] || this.params['questionOrder'] || '0';
      const mimeType = this.previewUrl.split(';')[0].split(':')[1];
      const extension = Convert.getExtension(mimeType)[0];
      const filename = `${this.getValue('t')}/${this.getValue('t')}_${questionOrder}.${extension}`;

      const payload = {
        idTrade: this.getValue('t'),
        filename,
        contentType: mimeType,
      };

      this.fileUploadService
        .postGeneratePresignedUrlUpload(payload, 'COLLECT_TVF.postGeneratePresignedUrl')
        .subscribe({
          next: async (response) => {
            try {
              const file = await this.fileUploadService.convertSrcToFile(
                this.previewUrl!,
                filename
              );
              await this.fileUploadService.uploadFile(file, response.url).toPromise();

              await this.fileUploadService
                .updateTradeGallery(
                  { idTrade: this.getValue('t'), filename },
                  'COLLECT_TVF.postRegisterUploadedMedia'
                )
                .toPromise();
              this.uploadSuccess = true;
              this.toastr.success('Evidência enviada com sucesso!');
            } catch (err) {
              this.handleUploadError(err);
            } finally {
              this.isUploading = false;
            }
          },
          error: (err) => {
            this.handleUploadError(err);
            this.isUploading = false;
          },
        });
    } catch (error) {
      console.error(error);
      this.toastr.error('Erro ao processar a imagem.');
      this.isUploading = false;
    }
  }

  private handleUploadError(error: any) {
    const message = error?.errors?.[0] || error?.error?.errors?.[0] || 'Erro ao realizar upload.';
    this.toastr.error(message);
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        this.previewUrl = reader.result as string;
        this.uploadEvidence();
      };
      reader.readAsDataURL(file);
    }
  }

  openCamera(): void {
    alert('Abrir câmera - implemente com plugin Capacitor ou input capture="camera"');
  }

  removePreview(): void {
    this.previewUrl = null;
  }

  getValue(key: string): any {
    return this.params[key] ?? null;
  }

  hasQuestion(): boolean {
    return !!this.params['question'] && typeof this.params['question'] === 'object';
  }
}
